import { defineComponent as _defineComponent } from 'vue';
import { createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, Transition as _Transition, withCtx as _withCtx } from "vue";
const _hoisted_1 = { class: "welcomePage" };
const _hoisted_2 = { class: "progressBar" };
const _hoisted_3 = { class: "stage" };
const _hoisted_4 = { class: "pills" };
const _hoisted_5 = ["disabled", "onClick"];
const _hoisted_6 = {
    key: 0,
    class: "anchor-bottom"
};
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { DotLottie } from '@lottiefiles/dotlottie-web';
import { useAuth } from '@/composables/useAuth';
import { useQuizApi } from '@/composables/useQuizApi';
import HeaderBlock from './elements/HeaderBlock.vue';
import MGButton from './elements/MGButton.vue';
const INTRO_MS = 800;
const STEP_SECONDS = 5;
export default /*@__PURE__*/ _defineComponent({
    __name: 'SignInWelcomePage',
    setup(__props) {
        DotLottie.setWasmUrl(require('@lottiefiles/dotlottie-web/dotlottie-player.wasm'));
        const steps = [
            {
                label: 'Save',
                colour: 'var(--lavender-100)',
                caption: 'Save on the things you already buy, with discounted gift cards for 170+ retailers and savings from 1.5% to 28%.',
                lottie: require('@/assets/onboarding/pillar-save-animation.lottie')
            },
            {
                label: 'Organise',
                colour: 'var(--bright-blue-1)',
                caption: 'See your accounts, cards, savings and other finances together in one simple view.',
                lottie: require('@/assets/onboarding/pillar-organise-animation.lottie')
            },
            {
                label: 'Grow',
                colour: 'var(--teal-100)',
                caption: 'Ask questions about your money and get straightforward answers, whenever you need them.',
                lottie: require('@/assets/onboarding/pillar-grow-animation.lottie')
            }
        ];
        const router = useRouter();
        useAuth();
        const { getQuestionnaireByKey } = useQuizApi();
        /* -1 = the intro, before any step lights up */
        const stepIndex = ref(-1);
        const starting = ref(false);
        /* True once the auto-play reaches the last step; from then on the pills replay their step instead */
        const introDone = ref(false);
        const lottieCanvas = ref(null);
        /* 0–1 through the current step's animation, from the player's own frame events */
        const stepProgress = ref(0);
        let player = null;
        let timer = null;
        const step = computed(() => steps[stepIndex.value] || null);
        const isLastStep = computed(() => stepIndex.value === steps.length - 1);
        function segmentProgress(index) {
            if (index < stepIndex.value)
                return 1;
            return index === stepIndex.value ? stepProgress.value : 0;
        }
        function nextStep() {
            if (stepIndex.value < steps.length - 1)
                stepIndex.value++;
        }
        function stopCurrent() {
            if (timer)
                clearTimeout(timer);
            timer = null;
            player?.destroy();
            player = null;
        }
        async function playStep() {
            stopCurrent();
            stepProgress.value = 0;
            if (!step.value)
                return;
            await nextTick();
            if (!lottieCanvas.value)
                return;
            const current = new DotLottie({ canvas: lottieCanvas.value, src: step.value.lottie, autoplay: true, loop: false });
            player = current;
            /* Every step lasts STEP_SECONDS, whatever length the file was exported at */
            current.addEventListener('load', () => current.setSpeed(current.duration / STEP_SECONDS));
            current.addEventListener('frame', (e) => { stepProgress.value = e.currentFrame / current.totalFrames; });
            current.addEventListener('complete', () => { stepProgress.value = 1; });
            /* During the auto-play each step moves on when its animation completes; the last one stays put for Continue */
            if (!introDone.value) {
                current.addEventListener('complete', nextStep);
                /* A file that fails to load mustn't strand the member on this step */
                current.addEventListener('loadError', () => { timer = setTimeout(nextStep, STEP_SECONDS * 1000); });
            }
        }
        watch(stepIndex, () => {
            if (isLastStep.value)
                introDone.value = true;
            playStep();
        });
        function selectStep(index) {
            if (index === stepIndex.value)
                playStep();
            else
                stepIndex.value = index;
        }
        function startSurvey() {
            starting.value = true;
            getQuestionnaireByKey('OB_MONEY', (q) => {
                router.push({ path: '/Questionnaire/' + q.id, query: { theme: 'onboarding', allowBack: 'false', escapeRoute: 'money' } });
            }, () => {
                starting.value = false;
            });
        }
        onMounted(() => {
            timer = setTimeout(nextStep, INTRO_MS);
        });
        onBeforeUnmount(stopCurrent);
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createVNode(HeaderBlock, {
                    title: "Welcome to Money Guided",
                    color: "white",
                    border: 0,
                    transparent: true
                }),
                _createElementVNode("div", _hoisted_1, [
                    _createElementVNode("div", _hoisted_2, [
                        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(steps, (s, i) => {
                            return _createElementVNode("div", {
                                key: s.label,
                                class: "progressSegment"
                            }, [
                                _createElementVNode("div", {
                                    class: "progressFill",
                                    style: _normalizeStyle({ width: segmentProgress(i) * 100 + '%' })
                                }, null, 4)
                            ]);
                        }), 64))
                    ]),
                    _createElementVNode("div", _hoisted_3, [
                        (step.value)
                            ? (_openBlock(), _createElementBlock("canvas", {
                                key: stepIndex.value,
                                ref_key: "lottieCanvas",
                                ref: lottieCanvas,
                                class: "stageMedia"
                            }))
                            : _createCommentVNode("", true)
                    ]),
                    _createElementVNode("div", _hoisted_4, [
                        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(steps, (s, i) => {
                            return _createElementVNode("button", {
                                key: s.label,
                                class: _normalizeClass(["pill", { current: i === stepIndex.value }]),
                                style: _normalizeStyle({ background: s.colour }),
                                disabled: !introDone.value,
                                onClick: ($event) => (selectStep(i))
                            }, _toDisplayString(s.label), 15, _hoisted_5);
                        }), 64))
                    ]),
                    _createVNode(_Transition, {
                        name: "fade",
                        mode: "out-in"
                    }, {
                        default: _withCtx(() => [
                            (step.value)
                                ? (_openBlock(), _createElementBlock("p", {
                                    key: stepIndex.value,
                                    class: "caption"
                                }, _toDisplayString(step.value.caption), 1))
                                : _createCommentVNode("", true)
                        ]),
                        _: 1
                    }),
                    _createVNode(_Transition, { name: "rise" }, {
                        default: _withCtx(() => [
                            (introDone.value)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                                    _createVNode(MGButton, {
                                        text: "Continue",
                                        neutral: true,
                                        arrow: true,
                                        fullWidth: true,
                                        disabled: starting.value,
                                        onClick: startSurvey
                                    }, null, 8, ["disabled"])
                                ]))
                                : _createCommentVNode("", true)
                        ]),
                        _: 1
                    })
                ])
            ], 64));
        };
    }
});
